import { BitkitLink, BitkitPagination, BitkitTable } from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { Collapsible } from '@chakra-ui/react/collapsible';
import { Separator } from '@chakra-ui/react/separator';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { SystemStyleObject } from '@chakra-ui/react/styled-system';
import { Fragment, ReactNode } from 'react';

import { nextSortDirection } from '@/hooks/useTableSort';

import DataTableRowMobile from './components/DataTableRowMobile';
import DataTableSortMobile from './components/DataTableSortMobile';
import DataTableLoadMore, { DataTableLoadMoreProps } from './DataTableLoadMore';
import {
  DataTableClickableRow,
  DataTableContainerProps,
  DataTableExpandableRow,
  DataTableHeader,
  DataTablePaginationProps,
  DataTableRow,
  DataTableSort,
  DataTableTableProps,
} from './types/DataTable.type';

/** v1 `Pagination`'s options, kept so the wrapper's call sites keep the page sizes they had. */
const DEFAULT_ITEMS_PER_PAGE_OPTIONS = [10, 20, 50];

interface CompactRowProps extends BoxProps {
  headerWidth?: BoxProps['w'];
}
type DataTableProps = {
  dataTestId?: string;
  filter?: ReactNode;
  headers: DataTableHeader[];
  isCompact: boolean;
  loadMore?: DataTableLoadMoreProps['loadMore'];
  pagination?: DataTablePaginationProps;
  rows?: DataTableRow[];
  skeletonRowCount?: number;
  emptyState?: ReactNode;
  sort?: DataTableSort;
  tableProps?: DataTableTableProps;
  tableContainerProps?: DataTableContainerProps;
  compactRowProps?: CompactRowProps;
};

const paginationProps = ({
  itemsPerPageOptions,
  page,
  perPage,
  setPage,
  setPerPage,
  totalCount,
}: DataTablePaginationProps) => ({
  onPageChange: setPage,
  onPageSizeChange: setPerPage,
  page,
  pageSize: perPage,
  pageSizeOptions: itemsPerPageOptions || DEFAULT_ITEMS_PER_PAGE_OPTIONS,
  totalItems: totalCount,
});

const DataTable = ({ isCompact, rows, skeletonRowCount = 5, dataTestId, ...props }: DataTableProps) => {
  const rowsWithSkeletonFallback =
    rows ||
    Array.from({ length: skeletonRowCount }).map(() => {
      return {
        fields: props.headers.reduce((previous, { key }) => {
          return {
            ...previous,
            [key]: <Skeleton height="32" width="100%" />,
          };
        }, {}),
      };
    });

  return isCompact ? (
    <DataTableMobile {...props} rows={rowsWithSkeletonFallback} dataTestId={dataTestId} />
  ) : (
    <DataTableDesktop {...props} rows={rowsWithSkeletonFallback} dataTestId={dataTestId} />
  );
};

type DataTableMobileProps = Omit<DataTableProps, 'isCompact' | 'rows'> & Required<Pick<DataTableProps, 'rows'>>;

export const DataTableMobile = ({
  compactRowProps = {},
  filter,
  headers,
  loadMore,
  pagination,
  rows,
  sort,
  tableProps,
  dataTestId,
}: DataTableMobileProps) => {
  const { headerWidth, borderTopRadius, ...compactRowBoxProps } = compactRowProps;
  return (
    <Box display="flex" flexDirection="column" gap="24" data-testid={dataTestId} {...compactRowBoxProps}>
      {filter}
      {sort && <DataTableSortMobile headers={headers} sort={sort} />}
      <Box
        borderColor="border/regular"
        borderRadius="8"
        borderWidth={tableProps?.variant === 'card' ? '0' : '1'}
        display="flex"
        flexDirection="column"
        borderTopRadius={borderTopRadius}
      >
        {rows.map((row, index) => {
          return (
            <Fragment key={row.rowKey ?? index}>
              {index > 0 && <Separator />}
              <DataTableRowMobile headerWidth={headerWidth} row={row} headers={headers} />
            </Fragment>
          );
        })}

        {loadMore && (
          <>
            <Separator />
            <Box paddingInline="24" paddingBlock="12">
              <DataTableLoadMore loadMore={loadMore} rowsLength={rows.length} />
            </Box>
          </>
        )}
        {pagination && (
          <>
            <Separator />
            {/* Every pagination slot is flexShrink: 0 under a root that clips, so the items-per-page block
                would push the page controls out of the mobile column. */}
            <BitkitPagination {...paginationProps(pagination)} onPageSizeChange={undefined} variant="card" />
          </>
        )}
      </Box>
    </Box>
  );
};

type DataTableDesktopProps = Omit<DataTableProps, 'isCompact' | 'rows'> & Required<Pick<DataTableProps, 'rows'>>;

const RowCells = ({ headers, row }: Pick<DataTableDesktopProps, 'headers'> & { row: DataTableRow }) => (
  <>
    {headers.map(({ key: headerKey, isNumeric, isActionButton, width }) => (
      <BitkitTable.Cell
        key={headerKey}
        width={width || (isActionButton ? '32' : undefined)}
        textAlign={isNumeric ? 'end' : undefined}
      >
        {row.fields[headerKey]}
      </BitkitTable.Cell>
    ))}
  </>
);

const ExpandableRow = ({
  headers,
  tableColumnsCount,
  tableHasExpandableRows,
  row,
}: Pick<DataTableDesktopProps, 'headers'> & {
  tableColumnsCount: number;
  tableHasExpandableRows: boolean;
  row: DataTableExpandableRow;
}) => {
  if (!row.expandableContent) {
    return (
      <BitkitTable.Row>
        {tableHasExpandableRows && <BitkitTable.Cell />}
        <RowCells headers={headers} row={row} />
      </BitkitTable.Row>
    );
  }

  return (
    <BitkitTable.ExpandableRow
      colSpan={tableColumnsCount}
      defaultOpen={row.defaultIsExpanded}
      expandedContent={
        // BitkitTable.ExpandableRow keeps its collapsed content mounted, where v1's `Tr` unmounted it. Call
        // sites rely on that — a collapsed row must not run its content's effects or requests — so the
        // row's own Collapsible context gates it back.
        <Collapsible.Context>
          {({ visible }) =>
            visible && (
              <Box whiteSpace="break-spaces">
                {typeof row.expandableContent === 'function' ? row.expandableContent(row) : row.expandableContent}
              </Box>
            )
          }
        </Collapsible.Context>
      }
    >
      <RowCells headers={headers} row={row} />
    </BitkitTable.ExpandableRow>
  );
};

/**
 * Mirrors bitkit's `linkedRow`/`linkCell` recipes for the row shape it has no component for: one
 * trigger stretched over the whole row, with everything else interactive lifted above it so the
 * action cell's menu opens instead of the row firing. Keeping the handler on a real button is also
 * what gives the row the tab stop and Enter that a bare `<tr onClick>` has neither of.
 */
const clickableRowStyles: SystemStyleObject = {
  '& :where(a, button:not([data-row-trigger]), input, label, select)': { position: 'relative', zIndex: '1' },
};

const rowTriggerStyles: SystemStyleObject = {
  appearance: 'none',
  background: 'none',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  font: 'inherit',
  padding: 0,
  textAlign: 'start',
  _after: { content: '""', inset: 0, position: 'absolute' },
  _focusVisible: {
    outline: 'none',
    _after: {
      outline: 'var(--focus-ring-width) var(--focus-ring-style) var(--focus-ring-color)',
      outlineOffset: 'calc(var(--focus-ring-width) * -1)',
    },
  },
};

const ClickableTableRow = ({
  fields,
  href,
  onClick,
  headers,
  rowKey,
}: DataTableClickableRow & Pick<DataTableDesktopProps, 'headers'>) => {
  const [header, ...rest] = headers;
  const cells = rest.map(({ key: headerKey, isNumeric, isActionButton }) => (
    <BitkitTable.Cell
      key={headerKey}
      width={isActionButton ? '32' : undefined}
      textAlign={isNumeric ? 'end' : undefined}
      // The row's stretched control covers this cell too, and both row shapes lift only standard
      // controls back above it. An action cell takes whatever the call site puts there, so it is
      // lifted whole instead — the shape v1's `ClickableRow` used.
      position={isActionButton ? 'relative' : undefined}
      zIndex={isActionButton ? '1' : undefined}
    >
      {fields[headerKey]}
    </BitkitTable.Cell>
  ));

  // Only an href row is a link row. An onClick row runs a handler, so it gets a button instead —
  // laid out the way BitkitTable.LinkedRow lays out its link, since bitkit has no such row.
  if (!href) {
    return (
      <BitkitTable.Row position="relative" css={clickableRowStyles} key={rowKey}>
        <BitkitTable.Cell>
          <Box asChild css={rowTriggerStyles}>
            <button type="button" data-row-trigger onClick={onClick}>
              {fields[header.key]}
            </button>
          </Box>
        </BitkitTable.Cell>
        {cells}
      </BitkitTable.Row>
    );
  }

  return (
    <BitkitTable.LinkedRow>
      <BitkitTable.LinkedRow.LinkCell>
        <BitkitLink href={href}>{fields[header.key]}</BitkitLink>
      </BitkitTable.LinkedRow.LinkCell>
      {cells}
    </BitkitTable.LinkedRow>
  );
};

const ColumnHeader = ({ header, sort }: { header: DataTableHeader; sort?: DataTableSort }) => {
  const {
    key,
    label,
    isActionButton: _isActionButton,
    isPrimary: _isPrimary,
    sortDirectionLabels: _sortDirectionLabels,
    isNumeric,
    isSortable,
    tooltip,
    ...rest
  } = header;
  const textAlign = isNumeric ? 'end' : undefined;

  // BitkitTable.SortableColumnHeader renders the direction it is given rather than cycling it, so the
  // asc → desc → none cycle v1's `Th` owned has to be advanced by the caller.
  if (isSortable && sort) {
    const direction = sort.sortHeader === key && sort.sortDirection !== 'other' ? sort.sortDirection : 'none';

    return (
      <BitkitTable.SortableColumnHeader
        {...rest}
        direction={direction || 'none'}
        onSort={() => sort.onSortClick(key, nextSortDirection(direction))}
        textAlign={textAlign}
      >
        {label}
      </BitkitTable.SortableColumnHeader>
    );
  }

  // The definition trigger is the column title itself, which the tooltip renders as text — same
  // constraint v1's `Th` had, where a non-string title logged and dropped the tooltip.
  if (tooltip && typeof label === 'string') {
    return (
      <BitkitTable.DefinitionColumnHeader {...rest} method={tooltip.method} text={tooltip.label} textAlign={textAlign}>
        {label}
      </BitkitTable.DefinitionColumnHeader>
    );
  }

  return (
    <BitkitTable.ColumnHeader {...rest} textAlign={textAlign}>
      {label}
    </BitkitTable.ColumnHeader>
  );
};

const DataTableDesktop = ({
  filter,
  headers,
  loadMore,
  pagination,
  rows,
  sort,
  tableContainerProps = {},
  tableProps: externalTableProps = {},
  emptyState,
  dataTestId,
}: DataTableDesktopProps) => {
  const { disableRowHover, isFixed, ...tableProps } = filter
    ? { ...externalTableProps, borderStartStartRadius: '0', borderStartEndRadius: '0' }
    : { ...externalTableProps };

  const tableHasExpandableRows = !!rows.find((row) => {
    return 'expandableContent' in row && row.expandableContent !== undefined;
  });

  const tableColumnsCount = headers.length + (tableHasExpandableRows ? 1 : 0);

  return (
    <>
      {filter}
      {emptyState && rows.length === 0 ? (
        emptyState
      ) : (
        <BitkitTable.ScrollArea {...tableContainerProps}>
          <BitkitTable.Root
            {...tableProps}
            data-testid={dataTestId}
            rowHover={!disableRowHover}
            tableLayout={isFixed ? 'fixed' : undefined}
          >
            <BitkitTable.Header>
              <BitkitTable.Row>
                {tableHasExpandableRows && <BitkitTable.ColumnHeader width="48" />}
                {headers.map((header) => (
                  <ColumnHeader key={header.key} header={header} sort={sort} />
                ))}
              </BitkitTable.Row>
            </BitkitTable.Header>
            <BitkitTable.Body>
              {rows.map((row, idx) => {
                if (('onClick' in row && row.onClick) || ('href' in row && row.href)) {
                  return <ClickableTableRow {...row} key={row.rowKey ?? idx} headers={headers} />;
                }
                return (
                  <ExpandableRow
                    key={row.rowKey ?? idx}
                    row={row as DataTableExpandableRow}
                    headers={headers}
                    tableColumnsCount={tableColumnsCount}
                    tableHasExpandableRows={tableHasExpandableRows}
                  />
                );
              })}
            </BitkitTable.Body>
            {(loadMore || pagination) && (
              <BitkitTable.Footer>
                {loadMore && (
                  <BitkitTable.Row>
                    <BitkitTable.Cell
                      colSpan={tableColumnsCount}
                      height="auto"
                      paddingBlock="12"
                      paddingInline="16"
                      textAlign="center"
                    >
                      <DataTableLoadMore loadMore={loadMore} rowsLength={rows.length} />
                    </BitkitTable.Cell>
                  </BitkitTable.Row>
                )}
                {pagination && (
                  <BitkitTable.Row>
                    <BitkitTable.Cell colSpan={tableColumnsCount}>
                      <BitkitPagination {...paginationProps(pagination)} variant="table" />
                    </BitkitTable.Cell>
                  </BitkitTable.Row>
                )}
              </BitkitTable.Footer>
            )}
          </BitkitTable.Root>
        </BitkitTable.ScrollArea>
      )}
    </>
  );
};

export default DataTable;
